import audioButton from '@/assets/marketing/audio-button.svg';
import blueWhalePng from '@/assets/marketing/blue-whale.png';
import catPng from '@/assets/marketing/cat.png';
import iconSrc from '@/assets/marketing/icon.png';
import txivPng from '@/assets/marketing/txiv.png';
import yellowSunPng from '@/assets/marketing/yellow-sun.png';
import { resolveFlashcardFrame } from '@/lib/flashcard-frames';

/** Same words, art, and frames as the in-app Colors / Family / Animals decks. */
const HERO_CARDS = {
  daj: { word: 'daj', gloss: 'yellow', color: 'YELLOW', image: yellowSunPng },
  txiv: { word: 'txiv', gloss: 'dad', color: 'LIGHT_ORANGE', image: txivPng },
  xiav: { word: 'xiav', gloss: 'blue', color: 'BLUE', image: blueWhalePng },
  miv: { word: 'miv', gloss: 'cat', color: 'LIGHT_RED', image: catPng },
} as const;

type HeroCard = (typeof HERO_CARDS)[keyof typeof HERO_CARDS];

function PhoneChrome({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`relative w-[260px] overflow-hidden rounded-[2.4rem] border-[10px] border-[#1E1E1E] bg-[#ecffe6] shadow-[0_24px_50px_rgba(31,86,16,0.28)] ${className}`}
    >
      <div className="absolute left-1/2 top-2 z-10 h-5 w-28 -translate-x-1/2 rounded-full bg-[#1E1E1E]" />
      <div className="pointer-events-none absolute inset-x-6 top-3 z-20 flex justify-between text-[10px] font-bold text-[#1E1E1E]/50">
        <span>9:41</span>
        <span>●●●</span>
      </div>
      {children}
      <div className="absolute bottom-2 left-1/2 h-1.5 w-24 -translate-x-1/2 rounded-full bg-[#1E1E1E]/80" />
    </div>
  );
}

function ProductFlashcard({
  card,
  variant,
  className = '',
}: {
  card: HeroCard;
  variant: 'phone' | 'mini';
  className?: string;
}) {
  const frame = resolveFlashcardFrame(card.color);
  const phone = variant === 'phone';
  return (
    <article
      className={`aspect-[375/645] ${phone ? 'relative' : ''} ${className}`}
      style={{ color: frame.ink }}
    >
      <img
        src={frame.frame}
        alt=""
        className="absolute inset-0 h-full w-full object-fill"
        fetchPriority={phone ? 'high' : undefined}
      />
      <div
        className={`relative flex h-full flex-col items-center justify-center text-center ${
          phone ? 'px-8 pb-8 pt-10' : 'px-3 pb-4 pt-5'
        }`}
      >
        <img
          src={card.image}
          alt=""
          className={`object-contain ${phone ? 'mb-5 max-h-40 w-[70%]' : 'mb-1.5 max-h-[42%] w-[68%]'}`}
          fetchPriority={phone ? 'high' : undefined}
        />
        <p
          className={`font-nunito font-extrabold leading-tight ${
            phone ? 'text-4xl' : 'text-[15px] leading-none'
          }`}
        >
          {card.word}
        </p>
        <p
          className={`font-nunito italic ${
            phone ? 'mt-2 text-2xl' : 'mt-1 text-[9px] leading-none'
          }`}
        >
          {card.gloss}
        </p>
        {phone ? <img src={audioButton} alt="" className="mt-8 h-11 w-11" /> : null}
      </div>
    </article>
  );
}

export function FlashcardPhone({ className = '' }: { className?: string }) {
  return (
    <PhoneChrome className={className}>
      <div className="px-4 pb-7 pt-10">
        <div className="mb-3 flex items-center justify-between px-0.5">
          <p className="text-[11px] font-extrabold text-primary">← Colors</p>
          <p className="text-[11px] font-extrabold text-slate-500">3 / 9</p>
        </div>
        <ProductFlashcard card={HERO_CARDS.daj} variant="phone" className="mx-auto w-full shadow-md" />
      </div>
    </PhoneChrome>
  );
}

export function WebLaptop({ className = '' }: { className?: string }) {
  const tiles = [
    { title: 'Colors', hue: '#FFF6D6' },
    { title: 'Animals', hue: '#ECFFE6' },
    { title: 'Stories', hue: '#E8F1FA' },
    { title: 'Games', hue: '#FFE8E0' },
  ];
  return (
    <div className={`w-full max-w-[460px] ${className}`}>
      <div className="overflow-hidden rounded-t-2xl border border-[#d5e4cf] bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-[#e4efe0] bg-[#f3fbf0] px-4 py-2.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          <div className="ml-3 flex-1 rounded-md bg-white px-3 py-1 text-[11px] font-semibold text-slate-400">
            hmonglusapp.com/app
          </div>
        </div>
        <div className="bg-[#f3fbf0] p-5">
          <div className="mb-4 flex items-center gap-2">
            <img src={iconSrc} alt="" className="h-8 w-8 rounded-lg bg-white" />
            <div>
              <p className="text-sm font-extrabold text-hmong-ink">Learning Hub</p>
              <p className="text-[11px] font-semibold text-primary">White Hmong · English</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {tiles.map((tile) => (
              <div key={tile.title} className="rounded-2xl p-4 shadow-sm" style={{ background: tile.hue }}>
                <p className="text-[10px] font-bold uppercase tracking-wide text-primary">Lesson</p>
                <p className="mt-2 text-base font-bold">{tile.title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mx-auto h-3 w-[92%] rounded-b-xl bg-[#d8e7d2]" />
      <div className="mx-auto h-2 w-40 rounded-b-lg bg-[#c4d8bc]" />
    </div>
  );
}

export function HeroDevices() {
  return (
    <div className="relative mx-auto flex h-[420px] w-full max-w-[520px] items-center justify-center sm:h-[520px]">
      <ProductFlashcard
        card={HERO_CARDS.txiv}
        variant="mini"
        className="absolute left-0 top-12 hidden w-32 -rotate-12 drop-shadow-xl sm:block"
      />
      <ProductFlashcard
        card={HERO_CARDS.xiav}
        variant="mini"
        className="absolute bottom-10 right-0 hidden w-32 rotate-12 drop-shadow-xl sm:block"
      />
      <ProductFlashcard
        card={HERO_CARDS.miv}
        variant="mini"
        className="absolute right-3 top-2 hidden w-28 rotate-6 drop-shadow-xl sm:block"
      />
      <FlashcardPhone className="relative z-10 scale-95 sm:scale-100" />
    </div>
  );
}
